import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

const AuthContext = createContext(null);

// The session lives in the httpOnly `gf_session` cookie now — the JWT is
// never stored in localStorage for regular browser sessions. We still cache
// the user record in localStorage to render an optimistic first paint before
// the /me round-trip resolves; this is not a secret.
const USER_CACHE_KEY = "gf_user";
const LEGACY_TOKEN_KEY = "gf_session"; // cleaned up on load

function api() { return process.env.REACT_APP_BACKEND_URL; }

function isPublicAuthUrl(u) {
  return (
    u.includes("/api/auth/login") ||
    u.includes("/api/auth/signup") ||
    u.includes("/api/auth/change-password") ||
    u.includes("/api/auth/me") ||           // expected to 401 for unauth visitors on the public home
    u.includes("/api/share/") ||
    u.includes("/api/public/")
  );
}

// Ensure every axios call from anywhere in the app sends the cookie. We do
// this once at module load rather than inside a React effect so early
// unauthenticated boot-time requests (before AuthProvider mounts) also carry
// credentials.
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  // undefined = still loading; null = logged out; { user } = logged in
  const [state, setState] = useState(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Purge any legacy localStorage-stored bearer token from previous
    // versions. The cookie is our single source of truth now.
    try {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    } catch { /* localStorage may be disabled — ignore */ }

    // Optimistic render with cached user while /me resolves
    try {
      const cached = JSON.parse(localStorage.getItem(USER_CACHE_KEY) || "null");
      if (cached && cached.id) setState({ user: cached });
    } catch { /* cache corrupt — ignore */ }

    // Trust the cookie. If it's missing/invalid the request 401s and we
    // land in the logged-out state.
    axios
      .get(`${api()}/api/auth/me`, { withCredentials: true })
      .then((res) => {
        setState({ user: res.data });
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem(USER_CACHE_KEY);
        setState(null);
      });
  }, []);

  // Attach global axios interceptors: force credentials + 401 auto-logout.
  useEffect(() => {
    const reqId = axios.interceptors.request.use((cfg) => {
      const u = cfg.url || "";
      if (u.includes("/api/")) {
        cfg.withCredentials = true;
      }
      return cfg;
    });
    const resId = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const u = err?.config?.url || "";
        // Auto-logout on unauthenticated API calls that aren't login/signup/
        // change-password/public share. Change-password 401 = wrong current
        // password (surface inline instead of logging user out).
        if (
          err?.response?.status === 401 &&
          u.includes("/api/") &&
          !isPublicAuthUrl(u)
        ) {
          localStorage.removeItem(USER_CACHE_KEY);
          setState(null);
          // Never auto-redirect from routes that are legitimately unauth-
          // reachable (public home, login, signup, share, /api/auth/me).
          const p = location.pathname;
          if (
            p !== "/" &&
            !p.startsWith("/login") &&
            !p.startsWith("/signup") &&
            !p.startsWith("/share/")
          ) {
            navigate("/login", { replace: true });
          }
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [navigate, location.pathname]);

  const value = useMemo(
    () => ({
      state, // undefined | null | { user }
      user: state?.user || null,
      isOwner: !!(state && state.user && state.user.role === "owner"),
      isMember: !!(state && state.user && state.user.role === "member"),
      login: async (email, password) => {
        // Server sets the httpOnly gf_session cookie AND returns { token, user }.
        // We keep the response for the user cache; the token itself is IGNORED
        // for regular browser sessions.
        const { data } = await axios.post(
          `${api()}/api/auth/login`,
          { email, password },
          { withCredentials: true },
        );
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user));
        setState({ user: data.user });
        return data.user;
      },
      signup: async (email, password) => {
        const { data } = await axios.post(
          `${api()}/api/auth/signup`,
          { email, password },
          { withCredentials: true },
        );
        return data;
      },
      logout: async () => {
        try {
          await axios.post(`${api()}/api/auth/logout`, null, { withCredentials: true });
        } catch (err) {
          // Cookie may already be gone (multi-tab logout race, offline, or
          // the network dropped between click and response). Not a real error
          // for the user — we're logging them out either way.
          console.warn("logout: server call did not complete", err?.message || err);
        }
        localStorage.removeItem(USER_CACHE_KEY);
        setState(null);
        // Land back on the public home (option B) rather than /login so
        // returning users can browse shared schedules without a
        // credentials prompt.
        navigate("/", { replace: true });
      },
      refreshMe: async () => {
        const res = await axios.get(`${api()}/api/auth/me`, { withCredentials: true });
        setState({ user: res.data });
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(res.data));
        return res.data;
      },
      // 2026-02 Phase D2 — merge partial preferences via
      // `PATCH /api/auth/preferences`.  Server whitelists the keys.
      // Non-fatal on failure: onboarding UI still dismisses in-memory,
      // so the user isn't stuck.  Returns the fresh user record.
      updatePreferences: async (partial) => {
        try {
          const res = await axios.patch(
            `${api()}/api/auth/preferences`,
            { preferences: partial },
            { withCredentials: true },
          );
          setState({ user: res.data });
          localStorage.setItem(USER_CACHE_KEY, JSON.stringify(res.data));
          return res.data;
        } catch (err) {
          console.warn("updatePreferences failed", err?.response?.data || err?.message);
          // Optimistic client-only merge so the UI still moves on.
          setState((prev) => {
            if (!prev?.user) return prev;
            const merged = { ...prev.user, preferences: { ...(prev.user.preferences || {}), ...partial } };
            localStorage.setItem(USER_CACHE_KEY, JSON.stringify(merged));
            return { user: merged };
          });
          return null;
        }
      },
    }),
    [state, navigate]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function PrivateRoute({ children, ownerOnly = false }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        <span className="w-3 h-3 rounded-full border-2 border-slate-300 border-t-[#0F4C81] animate-spin mr-2" />
        Loading…
      </div>
    );
  }
  if (state === null) {
    const next = encodeURIComponent(location.pathname + location.search);
    window.location.replace(`/login?next=${next}`);
    return null;
  }
  // 2026-02 Phase D1 — hard-gate: any authed user with
  // `must_change_password: true` is bounced to /change-password-
  // required, EXCEPT when they're already on that page.  This
  // matches the backend AuthMiddleware allow-list (which only lets
  // /auth/me, /auth/logout, and /auth/change-password-forced through
  // when the flag is set) so direct-URL bypass attempts land here.
  if (state.user?.must_change_password && location.pathname !== "/change-password-required") {
    window.location.replace("/change-password-required");
    return null;
  }
  if (ownerOnly && state.user?.role !== "owner") {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-slate-600 text-sm"
        data-testid="owner-only-blocked"
      >
        This page is only available to the Owner.
      </div>
    );
  }
  return children;
}

// Auth-aware route element for `/`. Renders `authed` when a session
// exists, `unauthed` otherwise. NEVER redirects — logged-out visitors
// stay put on the public home. Shows a lightweight spinner while /me
// is in flight so we don't flash the wrong shell.
export function HomeRoute({ authed, unauthed }) {
  const { state } = useAuth();
  if (state === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        <span className="w-3 h-3 rounded-full border-2 border-slate-300 border-t-[#0F4C81] animate-spin mr-2" />
        Loading…
      </div>
    );
  }
  return state ? authed : unauthed;
}
